import * as React from "react";
import {useSearchParams} from "react-router-dom";
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    Input,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddBoxOutlined from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlined from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import Loading from "../../components/loading";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import useDebounce from "../../lib/UseDebounce";
import moment from "moment-timezone";
import {toast} from "react-toastify";
import GroupCapUpsert from "./groupCapUpsert";
import {
    applyCampaigns,
    deleteGroupCap,
    fetchGroupCapCampaigns,
    fetchGroupCaps,
    setGroupCapActive,
    setGroupCapPaused,
} from "./groupCapsService";

const DAYS = [
    {key: "monday", short: "Mo", ddd: "Mon", yDdd: "Tue", col: 1},
    {key: "tuesday", short: "Tu", ddd: "Tue", yDdd: "Wed", col: 2},
    {key: "wednesday", short: "We", ddd: "Wed", yDdd: "Thu", col: 3},
    {key: "thursday", short: "Th", ddd: "Thu", yDdd: "Fri", col: 4},
    {key: "friday", short: "Fr", ddd: "Fri", yDdd: "Sat", col: 5},
    {key: "saturday", short: "Sa", ddd: "Sat", yDdd: "Sun", col: 6},
    {key: "sunday", short: "Su", ddd: "Sun", yDdd: "Mon", col: 7},
] as const;

function getDayHeader(column: number) {
    const today = moment();
    let dow = parseInt(today.format("e"), 10);
    if (dow === 0) {
        dow = 7;
    }
    let diff = dow - column;
    if (column < dow) {
        diff = diff - 7;
    }
    const date = today.clone().add(-diff, "day").format("MM/DD");
    return {date, highlight: diff === 0 || diff === 7};
}

function capColor(isOn: boolean) {
    return isOn ? "green" : "red";
}

function ShowCurrent({row, dayOfWeek, dayOfWeekY, dayKey}: {row: any; dayOfWeek: string; dayOfWeekY: string; dayKey: string}) {
    const tz = row.campaigns?.[0]?.timeZone || "America/Los_Angeles";
    const dayOfWeekTZ = moment().tz(tz).format("ddd");
    const parts: React.ReactNode[] = [];

    if (dayOfWeekTZ === dayOfWeekY && row.totals) {
        parts.push(<span key="y" style={{color: "blue"}}>{row.totals.yesterday || 0}Y</span>);
    } else if (dayOfWeekTZ === dayOfWeek && row.totals) {
        const dailyCap = row[`${dayKey}Cap`];
        const hourlyCap = row[`${dayKey}HourlyCap`];
        if (dailyCap?.value != null) {
            const c = (row.totals.daily || 0) >= dailyCap.value ? "red" : "#023490";
            parts.push(<span key="d" style={{color: c}}>{row.totals.daily || 0}D</span>);
        }
        if (hourlyCap?.value != null) {
            const c = (row.totals.hourly || 0) >= hourlyCap.value ? "red" : "#023490";
            parts.push(<span key="h" style={{color: c, marginLeft: 4}}>{row.totals.hourly || 0}H</span>);
        }
    }

    if (dayOfWeekTZ === dayOfWeek && row.pacingProvider) {
        const current = 0;
        const pacingCap = row[`${dayKey}PacingCap`];
        const c = pacingCap?.value != null && current >= pacingCap.value ? "red" : "#023490";
        parts.push(<span key="p" style={{color: c, marginLeft: 4}}>{current.toFixed(1)}%</span>);
    }

    if (!parts.length) {
        return null;
    }
    return <div>{parts}</div>;
}

function DayCaps({row, dayKey}: {row: any; dayKey: string}) {
    const daily = row[`${dayKey}Cap`];
    const hourly = row[`${dayKey}HourlyCap`];
    const pacing = row[`${dayKey}PacingCap`];
    return (
        <>
            {daily?.value != null && <span style={{color: capColor(!!daily.isOn)}}>{daily.value}D</span>}
            {hourly?.value != null && <span style={{color: capColor(!!hourly.isOn)}}> {hourly.value}H</span>}
            {pacing?.value != null && <span style={{color: capColor(!!pacing.isOn)}}> {pacing.value}%</span>}
        </>
    );
}

export default function GroupCapsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions.includes(PermissionType.Admin);
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = React.useState(true);
    const [rows, setRows] = React.useState<any[]>([]);
    const [search, setSearch] = React.useState(() => searchParams.get("s") || "");
    const [isActive, setIsActive] = React.useState(() => (searchParams.get("a") || "1") === "1");
    const [hasCap, setHasCap] = React.useState(true);
    const [sort, setSort] = React.useState<{key: string; asc: boolean}>({key: "name", asc: true});
    const [upsert, setUpsert] = React.useState<any>(null);
    const [anchorEls, setAnchorEls] = React.useState<any>({});
    const [openIds, setOpenIds] = React.useState<Record<number, boolean>>({});

    React.useEffect(() => {
        document.title = "Group Caps | DN";
    }, []);

    const reload = React.useCallback(async (searchText = search) => {
        setLoading(true);
        const data = await fetchGroupCaps(searchText);
        const activeIds = data.filter((r: any) => r.isActive).map((r: any) => r.id);
        let withCampaigns = data.map((r: any) => ({...r, open: false, campaigns: null, totals: null}));
        if (activeIds.length) {
            const batches = await fetchGroupCapCampaigns(activeIds);
            const byId: Record<number, any[]> = {};
            batches.forEach((b: any) => {
                byId[b.groupCapsId] = b.campaigns;
            });
            withCampaigns = withCampaigns.map((r: any) =>
                r.isActive ? applyCampaigns(r, byId[r.id] || []) : r
            );
        }
        setRows(withCampaigns);
        setLoading(false);
    }, [search]);

    React.useEffect(() => {
        reload(search).then(() => {});
    }, []);

    useDebounce(() => {
        reload(search).then(() => {});
    }, [search], 1000);

    const toggleOpen = async (row: any) => {
        const nextOpen = !openIds[row.id];
        setOpenIds((prev) => ({...prev, [row.id]: nextOpen}));
        if (nextOpen && !row.campaigns) {
            const batches = await fetchGroupCapCampaigns([row.id]);
            const campaigns = batches[0]?.campaigns || [];
            setRows((prev) => prev.map((r) => r.id === row.id ? applyCampaigns(r, campaigns) : r));
        }
    };

    const filtered = React.useMemo(() => {
        return rows.filter((r) => r.isActive === isActive && r.hasCaps === hasCap);
    }, [rows, isActive, hasCap]);

    const sorted = React.useMemo(() => {
        const list = [...filtered];
        const {key, asc} = sort;
        const dir = asc ? 1 : -1;
        list.sort((a, b) => {
            if (key === "id" || key === "name") {
                const av = a[key];
                const bv = b[key];
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                return 0;
            }
            if (key === "isTriggered") {
                const av = a.isTriggered ? 1 : 0;
                const bv = b.isTriggered ? 1 : 0;
                return (av - bv) * dir;
            }
            const getTotal = (row: any, field: string) => row.totals ? (row.totals[field] ?? 0) : null;
            if (key === "hourly" || key === "daily" || key === "monthly") {
                const av = getTotal(a, key);
                const bv = getTotal(b, key);
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
                if (av !== bv) return (av - bv) * dir;
                const capKey = `${key}Cap`;
                return ((a[capKey]?.value || 0) - (b[capKey]?.value || 0)) * dir;
            }
            if (DAYS.some((d) => d.key === key)) {
                const dayCap = (row: any) => {
                    const d = row[`${key}Cap`]?.value;
                    const h = row[`${key}HourlyCap`]?.value;
                    const p = row[`${key}PacingCap`]?.value;
                    return (d != null ? d * 1440 : 0) + (h != null ? h * 60 : 0) + (p != null ? p : 0);
                };
                const av = dayCap(a);
                const bv = dayCap(b);
                return (av - bv) * dir;
            }
            return 0;
        });
        return list;
    }, [filtered, sort]);

    const toggleSort = (key: string) => {
        setSort((prev) => prev.key === key ? {key, asc: !prev.asc} : {key, asc: true});
    };

    const sortLabel = (label: React.ReactNode, key: string) => (
        <TableSortLabel
            active={sort.key === key}
            direction={sort.key === key && !sort.asc ? "desc" : "asc"}
            onClick={() => toggleSort(key)}
        >
            {label}
        </TableSortLabel>
    );

    const handleActive = async (row: any, active: boolean) => {
        setAnchorEls({});
        const ok = await setGroupCapActive(row.id, active);
        if (ok) {
            toast.success(`${row.name} Active updated successfully.`);
            await reload();
        }
    };

    const handleDelete = async (row: any) => {
        setAnchorEls({});
        if (row.isActive) {
            toast.error("Only inactive groups can be deleted");
            return;
        }
        if (!window.confirm(`Are you sure you want to permanently delete group ${row.name}?`)) {
            return;
        }
        const ok = await deleteGroupCap(row.id);
        if (ok) {
            toast.success(`${row.name} deleted successfully.`);
            await reload();
        }
    };

    const handlePause = async (row: any) => {
        if (!isAdmin) return;
        const next = !row.isPaused;
        const ok = await setGroupCapPaused(row.id, next);
        if (ok) {
            toast.success(`${row.name} Pause updated successfully.`);
            setRows((prev) => prev.map((r) => r.id === row.id ? {...r, isPaused: next} : r));
        }
    };

    const oldUrl = process.env.REACT_APP_OLD_URL || "https://www2.degreenexus.com/admin";

    return (
        <>
            <h2>Group Caps</h2>
            <FormGroup row={true} sx={{alignItems: "center", gap: 1, mb: 1}}>
                <FormControlLabel
                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} size="small"/>}
                    label="Active"
                />
                <FormControlLabel
                    control={<Switch checked={hasCap} onChange={(e) => setHasCap(e.target.checked)} size="small"/>}
                    label="Cap"
                />
                {isAdmin && (
                    <Button variant="contained" color="success" size="small" onClick={() => reload()}>
                        <ReplayIcon fontSize="small"/>&nbsp;Run
                    </Button>
                )}
                <FormControl variant="outlined" size="small">
                    <Input
                        id="search"
                        type="text"
                        spellCheck={false}
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                    />
                </FormControl>
                {isAdmin && (
                    <FormControl size="small" sx={{ml: "auto"}}>
                        <Button variant="contained" color="warning" onClick={() => setUpsert({})}>
                            <AddBoxOutlinedIcon/>&nbsp;Create Group
                        </Button>
                    </FormControl>
                )}
            </FormGroup>

            {loading ? <Loading/> : (
                <TableContainer
                    component={Paper}
                    sx={{maxWidth: 2000, maxHeight: "calc(100vh - 220px)", overflow: "auto"}}
                >
                    <Table size="small" stickyHeader sx={{
                        width: "100%",
                        "& .MuiTableCell-root": {fontSize: 12, py: 0.5, px: 0.75, whiteSpace: "nowrap", verticalAlign: "top"},
                    }}>
                        <TableHead>
                            <TableHeaderRow>
                                {isAdmin && <TableCell/>}
                                <TableCell>{sortLabel("ID", "id")}</TableCell>
                                <TableCell>{sortLabel("Name", "name")}</TableCell>
                                <TableCell>Active / Cap</TableCell>
                                <TableCell>{sortLabel("Triggered", "isTriggered")}</TableCell>
                                <TableCell>{sortLabel("Hour", "hourly")}</TableCell>
                                <TableCell>{sortLabel("Day", "daily")}</TableCell>
                                <TableCell>{sortLabel("Month", "monthly")}</TableCell>
                                {DAYS.map((d) => {
                                    const {date, highlight} = getDayHeader(d.col);
                                    return (
                                        <TableCell key={d.key}>
                                            {sortLabel(
                                                <span>
                                                    {d.short}{" "}
                                                    <span style={highlight ? {background: "yellow", fontWeight: 700} : undefined}>{date}</span>
                                                </span>,
                                                d.key
                                            )}
                                        </TableCell>
                                    );
                                })}
                                {isAdmin && <TableCell/>}
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((row) => (
                                <React.Fragment key={row.id}>
                                    <StripedTableRow>
                                        {isAdmin && (
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={() => toggleOpen(row)}>
                                                    {openIds[row.id]
                                                        ? <IndeterminateCheckBoxOutlined fontSize="small"/>
                                                        : <AddBoxOutlined fontSize="small"/>}
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>
                                            <Box sx={{fontSize: 14, lineHeight: 1.3}}>{row.name}</Box>
                                            <Box component="div" sx={{fontSize: 12, fontStyle: "italic", color: "text.secondary", lineHeight: 1.3}}>{row.notes}</Box>
                                        </TableCell>
                                        <TableCell>
                                            <span style={{color: row.isActive ? "green" : "red"}}>{row.isActive ? "Yes" : "No"}</span>
                                            {" / "}
                                            <span style={{color: row.hasCaps ? "green" : "red"}}>{row.hasCaps ? "Yes" : "No"}</span>
                                        </TableCell>
                                        <TableCell>
                                            {row.isTriggered && (
                                                <span style={{color: "red", verticalAlign: "middle"}}>
                                                    Yes <PanToolOutlinedIcon sx={{color: "red", fontSize: 22, verticalAlign: "middle"}}/>
                                                </span>
                                            )}
                                            {isAdmin && (
                                                <PauseIcon
                                                    sx={{
                                                        color: row.isPaused ? "red" : "#ccc",
                                                        cursor: "pointer",
                                                        fontSize: 22,
                                                        verticalAlign: "middle",
                                                        ml: 0.5,
                                                    }}
                                                    onClick={() => handlePause(row)}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{color: capColor(!!row.hourlyCap?.isOn)}}>
                                            {row.hourlyCap?.value}
                                            {row.hourlyCap?.value != null && row.totals && (
                                                <Box sx={{color: "#023490"}}>{row.totals.hourly}</Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{color: capColor(!!row.dailyCap?.isOn)}}>
                                            {row.dailyCap?.value}
                                            {row.dailyCap?.value != null && row.totals && (
                                                <Box sx={{color: "#023490"}}>{row.totals.daily}</Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{color: capColor(!!row.monthlyCap?.isOn)}}>
                                            {row.monthlyCap?.value}
                                            {row.monthlyCap?.value != null && row.totals && (
                                                <Box sx={{color: "#023490"}}>{row.totals.monthly}</Box>
                                            )}
                                        </TableCell>
                                        {DAYS.map((d) => (
                                            <TableCell key={d.key}>
                                                <DayCaps row={row} dayKey={d.key}/>
                                                <ShowCurrent row={row} dayOfWeek={d.ddd} dayOfWeekY={d.yDdd} dayKey={d.key}/>
                                            </TableCell>
                                        ))}
                                        {isAdmin && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => setAnchorEls({...anchorEls, [row.id]: e.currentTarget})}
                                                >
                                                    <MoreVertIcon fontSize="small"/>
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEls[row.id]}
                                                    open={!!anchorEls[row.id]}
                                                    onClose={() => setAnchorEls({...anchorEls, [row.id]: null})}
                                                >
                                                    <MenuItem onClick={() => { setAnchorEls({}); setUpsert(row); }}>Edit Group</MenuItem>
                                                    {!row.isActive && (
                                                        <MenuItem onClick={() => handleActive(row, true)}>Activate</MenuItem>
                                                    )}
                                                    {row.isActive && (
                                                        <MenuItem onClick={() => handleActive(row, false)}>De-Activate</MenuItem>
                                                    )}
                                                    {!row.isActive && (
                                                        <MenuItem onClick={() => handleDelete(row)}>Delete Group</MenuItem>
                                                    )}
                                                </Menu>
                                            </TableCell>
                                        )}
                                    </StripedTableRow>
                                    {openIds[row.id] && (
                                        <TableRow>
                                            <TableCell colSpan={100}>
                                                <Table size="small" sx={{"& .MuiTableCell-root": {fontSize: 12}}}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>ID</TableCell>
                                                            <TableCell>PR</TableCell>
                                                            <TableCell>Cmp</TableCell>
                                                            <TableCell>Broker</TableCell>
                                                            <TableCell>School</TableCell>
                                                            <TableCell>App</TableCell>
                                                            <TableCell>D.Cap</TableCell>
                                                            <TableCell>M.Cap</TableCell>
                                                            <TableCell>H</TableCell>
                                                            <TableCell>D</TableCell>
                                                            <TableCell>M</TableCell>
                                                            <TableCell>Y</TableCell>
                                                            <TableCell>App % 30d (Total)</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(row.campaigns || []).map((c: any) => (
                                                            <TableRow key={c.hpCampaignId}>
                                                                <TableCell>
                                                                    <a
                                                                        href={`/campaigns/${c.hpCampaignId}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {c.hpCampaignId}
                                                                    </a>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <a
                                                                        href={`/portalRedirectCampaigns?search=${encodeURIComponent(c.hpCampaignId)}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        PR
                                                                    </a>
                                                                </TableCell>
                                                                <TableCell>{c.hpCompany}</TableCell>
                                                                <TableCell>{c.brokerName} ({c.timeZone})</TableCell>
                                                                <TableCell>{c.schoolName}</TableCell>
                                                                <TableCell>{c.appId}</TableCell>
                                                                <TableCell>{c.dailyCap}</TableCell>
                                                                <TableCell>{c.monthlyCap}</TableCell>
                                                                <TableCell>{c.hourly}</TableCell>
                                                                <TableCell>{c.daily}</TableCell>
                                                                <TableCell>{c.monthly}</TableCell>
                                                                <TableCell>{c.yesterday}</TableCell>
                                                                <TableCell>
                                                                    {(c.appRate30d || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}%
                                                                    {" "}({c.total30d})
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {row.totals && (
                                                            <TableRow>
                                                                <TableCell colSpan={6}><b>Totals</b></TableCell>
                                                                <TableCell><b>{row.totals.dailyCap}</b></TableCell>
                                                                <TableCell><b>{row.totals.monthlyCap}</b></TableCell>
                                                                <TableCell><b>{row.totals.hourly}</b></TableCell>
                                                                <TableCell><b>{row.totals.daily}</b></TableCell>
                                                                <TableCell><b>{row.totals.monthly}</b></TableCell>
                                                                <TableCell><b>{row.totals.yesterday}</b></TableCell>
                                                                <TableCell>
                                                                    <b>
                                                                        {(row.totals.appRate30d || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}%
                                                                        {" "}({row.totals.total30d})
                                                                    </b>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <GroupCapUpsert group={upsert} setGroup={setUpsert} reload={() => reload()}/>
        </>
    );
}
